import React, { useEffect, useState } from 'react';
import { Card, ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import applicationService from '@/shared/services/applicationService';
import StatusPill from '@/components/common/StatusPill';
import { applicationStatusOf } from '@/shared/utils/applicationStatus';
import {
    awardOf,
    canResubmit,
    requestOutcomeOf,
} from '@/shared/utils/requestOutcome';
import DecisionHistory from '@/components/common/DecisionHistory';
import { useAuth } from '@/shared/contexts/AuthContext';
import LoadError from '@/components/common/LoadError';
import LoadingState from '@/components/common/LoadingState';

const SHOWN = 5;

const courseLabel = (app) =>
    app.course ? `${app.course.subject} ${app.course.courseNumber}` : 'Course';

/**
 * "Your support requests": the person's most recent submitted course support
 * applications with a plain-language status and the decisions the server has
 * released for them. Drafts have their own card. Renders nothing when the
 * person has only drafts; says so, with Retry, when the applications can't
 * be loaded.
 */
function RecentApplicationsCard() {
    const { userProfile } = useAuth();
    const [applications, setApplications] = useState(null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setFailed(false);
        Promise.resolve()
            .then(() => applicationService.getMyApplications())
            .then((apps) => {
                if (!cancelled) setApplications(apps || []);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [attempt]);

    if (failed) {
        return (
            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-transparent border-0">
                    <h2 className="h5 mb-0">Your support requests</h2>
                </Card.Header>
                <Card.Body>
                    <LoadError
                        what="your support requests"
                        onRetry={() => setAttempt((n) => n + 1)}
                        className="mb-0"
                    />
                </Card.Body>
            </Card>
        );
    }

    if (applications === null) {
        return <LoadingState rows={1} label="Loading your support requests" />;
    }

    // A request that waits on its faculty member comes first, as on the
    // Support requests page; the rest keep the server's order
    const submitted = applications
        .filter((a) => a.status !== 'draft')
        .sort(
            (a, b) =>
                Number(canResubmit(b, userProfile)) -
                Number(canResubmit(a, userProfile))
        );
    if (applications.length > 0 && submitted.length === 0) return null;

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
                <h2 className="h5 mb-0">Your support requests</h2>
            </Card.Header>
            <Card.Body>
                {submitted.length === 0 ? (
                    <>
                        <p>You have not requested support yet.</p>
                        <Link
                            to="/applications/start"
                            className="btn btn-primary btn-sm"
                        >
                            Start a request
                        </Link>
                    </>
                ) : (
                    <>
                        <ListGroup as="ul" variant="flush">
                            {submitted.slice(0, SHOWN).map((app) => {
                                const status = applicationStatusOf(
                                    app.status,
                                    app
                                );
                                return (
                                    <ListGroup.Item
                                        as="li"
                                        key={app._id}
                                        className="px-0 d-flex flex-wrap justify-content-between align-items-start gap-2"
                                    >
                                        <div>
                                            <strong>{courseLabel(app)}</strong>
                                            {app.course?.title && (
                                                <span className="text-muted ms-2">
                                                    {app.course.title}
                                                </span>
                                            )}
                                            {app.courseConfirmation
                                                ?.sectionNumber && (
                                                <span className="text-muted ms-2">
                                                    Section{' '}
                                                    {
                                                        app.courseConfirmation
                                                            .sectionNumber
                                                    }
                                                </span>
                                            )}
                                            <div className="mt-1">
                                                <StatusPill
                                                    family="request"
                                                    value={app.status}
                                                    application={app}
                                                    pill={
                                                        awardOf(app)
                                                            ? requestOutcomeOf(
                                                                  app
                                                              )
                                                            : null
                                                    }
                                                />
                                            </div>
                                            {status.meaning && (
                                                <small className="d-block text-muted">
                                                    {status.meaning}
                                                </small>
                                            )}
                                            {app.decisions?.length > 0 &&
                                                app.programPreferences?.length >
                                                    1 && (
                                                    <DecisionHistory
                                                        decisions={
                                                            app.decisions
                                                        }
                                                        compact
                                                    />
                                                )}
                                        </div>
                                        <Link
                                            to={`/applications/${app._id}`}
                                            className="btn btn-outline-primary btn-sm"
                                            aria-label={`View ${courseLabel(app)} application`}
                                        >
                                            View
                                        </Link>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                        <Link to="/applications">View all requests</Link>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}

export default RecentApplicationsCard;
